import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Spinner,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React from 'react';
import { TIN_VERDICT } from 'utils/taxValidationApi';

// RAT-1911: renders the tax registry's answer under the Tax ID field.
//
// The three outcomes are deliberately styled apart:
//  - the registry confirmed the number             -> success / warning
//  - the registry has no record of it (INVALID)    -> error + an explicit confirm
//  - the registry could not be reached (UNVERIFIED)-> info, never an error
//
// That last distinction is the point of AC7: presenting "we could not check" as
// a failure trains operators to click through the warning, which would make the
// whole check worthless.
const STATUS_BY_VERDICT = {
  [TIN_VERDICT.VALID]: 'success',
  [TIN_VERDICT.INVALID_SIMILAR]: 'warning',
  [TIN_VERDICT.VALID_REMOVED]: 'warning',
  [TIN_VERDICT.VALID_REVOKED]: 'warning',
  [TIN_VERDICT.INVALID]: 'error',
  [TIN_VERDICT.FORMAT_INVALID]: 'error',
  [TIN_VERDICT.UNVERIFIED]: 'info',
};

const TaxNumberValidationNotice = ({
  result,
  isChecking,
  isConfirmed,
  onConfirmChange,
  onAdoptRegistryName,
}) => {
  if (isChecking) {
    return (
      <Box mt="8px" display="flex" alignItems="center" gap="8px">
        <Spinner size="xs" />
        <Text fontSize="sm" color="gray.500">
          Checking the tax number against the tax registry...
        </Text>
      </Box>
    );
  }

  // No result, or a country with no registry behind it: say nothing rather than
  // nag about a check that was never possible.
  if (!result || result.verdict === TIN_VERDICT.NO_REGISTRY) {
    return null;
  }

  const status = STATUS_BY_VERDICT[result.verdict] || 'info';
  const messages =
    result.verdict === TIN_VERDICT.FORMAT_INVALID && result.formatErrors?.length
      ? result.formatErrors
      : [result.message];

  return (
    <Box mt="8px">
      <Alert status={status} borderRadius="12px" fontSize="sm">
        <AlertIcon />
        <Box>
          {messages.map((message) => (
            <Text key={message}>{message}</Text>
          ))}
          {result.verdict === TIN_VERDICT.INVALID_SIMILAR &&
            result.registryLegalName && (
              <Box mt="6px">
                <Text>
                  Registry legal name: <b>{result.registryLegalName}</b>
                </Text>
                <Button
                  mt="6px"
                  size="xs"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => onAdoptRegistryName(result.registryLegalName)}
                >
                  Use the registry legal name
                </Button>
              </Box>
            )}
          {result.verdict === TIN_VERDICT.INVALID && (
            <Checkbox
              mt="8px"
              isChecked={isConfirmed}
              onChange={(event) => onConfirmChange(event.target.checked)}
            >
              Save this tax number as entered
            </Checkbox>
          )}
          {result.formatWarnings?.map((warning) => (
            <Text key={warning} mt="4px">
              {warning}
            </Text>
          ))}
        </Box>
      </Alert>
    </Box>
  );
};

TaxNumberValidationNotice.propTypes = {
  result: PropTypes.shape({
    verdict: PropTypes.string,
    message: PropTypes.string,
    registryLegalName: PropTypes.string,
    formatErrors: PropTypes.arrayOf(PropTypes.string),
    formatWarnings: PropTypes.arrayOf(PropTypes.string),
  }),
  isChecking: PropTypes.bool,
  isConfirmed: PropTypes.bool,
  onConfirmChange: PropTypes.func.isRequired,
  onAdoptRegistryName: PropTypes.func.isRequired,
};

export default TaxNumberValidationNotice;
